import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/status";
import ChangePasswordDialog from "@/components/app/ChangePasswordDialog";
import PixSettingsDialog from "@/components/app/PixSettingsDialog";
import { KeyRound, QrCode, LogOut, ChevronRight } from "lucide-react";

// items: array de { key, label, icon, onClick, active }  |  { type:"fab", label, icon, onClick }
//                | { type:"menu", label, icon, links:[{label, icon, onClick}] }
export default function MobileTabBar({ items = [] }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pixOpen, setPixOpen] = useState(false);
  const canPix = user?.role === "manager" || user?.role === "franchisee";
  const menuItem = items.find((it) => it.type === "menu");

  return (
    <>
      <div className="h-20 sm:hidden" aria-hidden />
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        data-testid="mobile-tab-bar">
        <div className="flex items-end justify-around px-1 h-16">
          {items.map((it, i) => {
            if (it.type === "fab") {
              const Icon = it.icon;
              return (
                <button key={i} onClick={it.onClick} data-testid="mobile-tab-fab"
                  className="flex-1 flex flex-col items-center -mt-6">
                  <span className="h-14 w-14 rounded-full bg-[#1e7be0] text-white flex items-center justify-center shadow-lg ring-4 ring-white active:scale-95 transition-transform">
                    <Icon className="h-7 w-7" />
                  </span>
                  <span className="text-[10px] font-medium text-slate-500 mt-0.5">{it.label}</span>
                </button>
              );
            }
            const Icon = it.icon;
            const isMenu = it.type === "menu";
            const active = it.active;
            return (
              <button key={i} onClick={isMenu ? () => setMenuOpen(true) : it.onClick}
                data-testid={`mobile-tab-${it.key || it.label.toLowerCase()}`}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2 transition-colors ${active ? "text-[#1e7be0]" : "text-slate-500"}`}>
                <Icon className={`h-5 w-5 ${active ? "text-[#1e7be0]" : "text-slate-500"}`} />
                <span className={`text-[10px] ${active ? "font-semibold" : "font-medium"}`}>{it.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="mobile-more-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-base">{user?.name}</SheetTitle>
            <p className="text-xs text-slate-500 -mt-1">{ROLE_LABEL[user?.role] || user?.role}</p>
          </SheetHeader>
          <div className="mt-3 grid grid-cols-1 divide-y divide-slate-100">
            {(menuItem?.links || []).map((l, i) => {
              const LI = l.icon;
              return (
                <button key={i} onClick={() => { setMenuOpen(false); l.onClick?.(); }}
                  data-testid={`mobile-more-${l.key || i}`}
                  className="flex items-center gap-3 py-3 text-slate-700 active:bg-slate-50">
                  {LI && <LI className="h-5 w-5 text-[#1e7be0]" />}
                  <span className="text-sm flex-1 text-left">{l.label}</span>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              );
            })}
            <button onClick={() => { setMenuOpen(false); setPwdOpen(true); }}
              data-testid="mobile-more-password"
              className="flex items-center gap-3 py-3 text-slate-700 active:bg-slate-50">
              <KeyRound className="h-5 w-5 text-slate-500" />
              <span className="text-sm flex-1 text-left">Trocar senha</span>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </button>
            {canPix && (
              <button onClick={() => { setMenuOpen(false); setPixOpen(true); }}
                data-testid="mobile-more-pix"
                className="flex items-center gap-3 py-3 text-slate-700 active:bg-slate-50">
                <QrCode className="h-5 w-5 text-slate-500" />
                <span className="text-sm flex-1 text-left">Meu Pix</span>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </button>
            )}
            <button onClick={() => { setMenuOpen(false); logout(); }}
              data-testid="mobile-more-logout"
              className="flex items-center gap-3 py-3 text-red-600 active:bg-red-50">
              <LogOut className="h-5 w-5" />
              <span className="text-sm flex-1 text-left">Sair</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      {canPix && <PixSettingsDialog open={pixOpen} onOpenChange={setPixOpen} />}
    </>
  );
}
